import { useState } from "react";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Fab,
  Link,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import { Download, LockOpen, MoreVert, Upload } from "@mui/icons-material";

import { toThreeDecimals } from "../../../components/NewDetail/calculations/utils";
import SelectTime from "../../admin/Time/SelectTime";
import BreakMinutes from "../../admin/Time/BreakMinutes";
import NoWorkSwitch from "../../admin/Time/NoWorkSwitch";
import { PinInput } from "react-input-pin-code";
import { LoadingButton } from "@mui/lab";
import getTimeCardDocDefinition from "../../../pdf-helpers/TimeCardHelper";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import S3PresignedPdf from "../../../components/S3PresignedPdf";
import S3PresignedImage from "../../../components/S3PresignedImage";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc"; // ES 2015
import timezone from "dayjs/plugin/timezone"; // ES 2015
import isoWeek from "dayjs/plugin/isoWeek"; // ES 2015
import { useTheme } from "@emotion/react";
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(isoWeek);
const DEFAULT_TIMEZONE = "America/Phoenix";
dayjs.tz.setDefault(DEFAULT_TIMEZONE);

pdfMake.vfs = pdfFonts.pdfMake.vfs;

// isoDate is "YYYY-MM-DD"
// the isoDateParam will always be treated as a Phoenix Date
const TimeSummary = ({
  isAdmin,
  token, // NoAuth Token
  weeklyUserTimeSummary,
  approveTime,
  requestNewPin,
  enableSignWithPin = false,

  loading,
  enableUploadSignedTimeSummary = false,
  handleFileUpload,
  downloadUploadedSignedSummary,
  onReOpen,
}) => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
  const [pinValues, setPinValues] = useState(["", "", "", ""]);
  const [pinReady, setPinReady] = useState(false);

  const [actionsAnchor, setActionsAnchor] = useState(null);

  const handleCloseActionMenu = () => {
    setActionsAnchor(null);
  };

  const getPDF = async (useUnsignedVersion) => {
    try {
      const docDef = await getTimeCardDocDefinition(
        weeklyUserTimeSummary,
        useUnsignedVersion,
        token
      );
      let fileName = `WEEKLY_TIME_SUMMARY_${dayjs(weeklyUserTimeSummary.start)
        .tz(DEFAULT_TIMEZONE)
        .startOf("isoWeek")
        .format("YYYY-MM-DD")} - ${dayjs(weeklyUserTimeSummary.start)
        .tz(DEFAULT_TIMEZONE)
        .endOf("isoWeek")
        .format("YYYY-MM-DD")}.pdf`;
      pdfMake
        .createPdf(docDef) //.open();
        .download(fileName);
    } catch (error) {
      console.log("ERROR", error);
      throw Error("Unable to load PDF", { cause: error });
    }
  };

  if (!weeklyUserTimeSummary) return null;

  return (
    <Box sx={{ paddingLeft: "8px", paddingRight: "8px" }}>
      <Typography sx={{ marginTop: 0, textAlign: "center" }} variant="h5">
        Weekly Time Summary
      </Typography>
      <Typography sx={{ textAlign: "center" }}>
        {dayjs(weeklyUserTimeSummary.start)
          .tz(DEFAULT_TIMEZONE)
          .startOf("isoWeek")
          .format("MM/DD/YYYY")}{" "}
        -{" "}
        {dayjs(weeklyUserTimeSummary.start)
          .tz(DEFAULT_TIMEZONE)
          .endOf("isoWeek")
          .format("MM/DD/YYYY")}
      </Typography>
      {isMediumScreen ? (
        <TableContainer component={Box}>
          <Table sx={{}}>
            <TableBody>
              {weeklyUserTimeSummary && (
                <>
                  <TableRow>
                    <TableCell align="left" sx={{ fontWeight: "bold" }}>
                      Employee
                    </TableCell>
                    <TableCell align="left">
                      <Box
                        sx={{
                          width: "130px",
                          textTransform: "uppercase",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: "1",
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {weeklyUserTimeSummary.user.fullName}
                      </Box>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align="left" sx={{ fontWeight: "bold" }}>
                      Work Hours
                    </TableCell>
                    <TableCell align="left">
                      {toThreeDecimals(
                        Number(weeklyUserTimeSummary.totalWorkMinutes ?? 0) / 60
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align="left" sx={{ fontWeight: "bold" }}>
                      No-Work Days
                    </TableCell>
                    <TableCell align="left">
                      {Number(weeklyUserTimeSummary.totalNoWorkDays ?? 0)}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell align="left" sx={{ fontWeight: "bold" }}>
                      Signed
                    </TableCell>
                    <TableCell align="left">
                      {weeklyUserTimeSummary.employeeSignedOn
                        ? `${dayjs(weeklyUserTimeSummary.employeeSignedOn)
                            .tz(DEFAULT_TIMEZONE)
                            .format("MM/DD/YY")}`
                        : "No"}
                    </TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      ) : (
        <TableContainer component={Box}>
          <Table sx={{}}>
            <TableHead>
              <TableRow>
                <TableCell align="left">Employee</TableCell>
                <TableCell align="left">Work Hours</TableCell>
                <TableCell align="left">No-Work Days</TableCell>
                {/* <TableCell align="left">Admin Approved</TableCell> */}
                <TableCell align="left">Signed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {weeklyUserTimeSummary && (
                <TableRow>
                  <TableCell align="left">
                    <Box
                      sx={{
                        width: "130px",
                        textTransform: "uppercase",
                        fontWeight: "bold",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        display: "-webkit-box",
                        WebkitLineClamp: "1",
                        WebkitBoxOrient: "vertical",
                      }}
                    >
                      {weeklyUserTimeSummary.user.fullName}
                    </Box>
                  </TableCell>

                  <TableCell align="left">
                    {toThreeDecimals(
                      Number(weeklyUserTimeSummary.totalWorkMinutes ?? 0) / 60
                    )}
                  </TableCell>
                  <TableCell align="left">
                    {Number(weeklyUserTimeSummary.totalNoWorkDays ?? 0)}
                  </TableCell>

                  {/* <TableCell align="left">
                    {weeklyUserTimeSummary.adminApprovedOn
                      ? `${dayjs(weeklyUserTimeSummary.adminApprovedOn)
                          .tz(DEFAULT_TIMEZONE)
                          .format("MM/DD/YY")} by ${
                          weeklyUserTimeSummary.adminApprovedBy.fullName
                        }`
                      : ""}
                  </TableCell> */}
                  <TableCell align="left">
                    {weeklyUserTimeSummary.employeeSignedOn
                      ? `${dayjs(weeklyUserTimeSummary.employeeSignedOn)
                          .tz(DEFAULT_TIMEZONE)
                          .format("MM/DD/YY")}`
                      : ""}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Typography sx={{ marginTop: "32px" }} variant="h6">
        Time Entries
      </Typography>
      {isMediumScreen ? (
        <Box>
          {weeklyUserTimeSummary.timeEntries &&
            weeklyUserTimeSummary.timeEntries.map((timeEntry, index) => {
              return (
                <Accordion>
                  <AccordionSummary
                    // expandIcon={<ExpandMoreIcon />}

                    aria-controls="panel1-content"
                    id="panel1-header"
                  >
                    <Box sx={{ display: "flex", flexDirection: "row" }}>
                      <Box sx={{ width: "60px" }}>
                        {dayjs(timeEntry.date)
                          .tz(DEFAULT_TIMEZONE)
                          .format("ddd.")}
                      </Box>
                      <Box sx={{ width: "70px" }}>
                        {dayjs(timeEntry.date)
                          .tz(DEFAULT_TIMEZONE)
                          .format("MM/DD")}
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        {!timeEntry.noWork ? (
                          <>
                            {toThreeDecimals(
                              Number(timeEntry.workMinutes ?? 0) / 60
                            )}{" "}
                            Hours
                          </>
                        ) : (
                          <>No Work</>
                        )}
                      </Box>
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box>
                      {timeEntry.noWork ? (
                        <Typography>{timeEntry.noWorkReason}</Typography>
                      ) : (
                        <Box>
                          <Box sx={{ display: "flex", flexDirection: "row" }}>
                            <Box sx={{ width: "130px" }}>
                              <SelectTime
                                label="Start"
                                disabled={true}
                                size="small"
                                timezone={DEFAULT_TIMEZONE}
                                value={dayjs(timeEntry.start).tz(
                                  DEFAULT_TIMEZONE
                                )}
                                maxTime={dayjs(timeEntry.stop).tz(
                                  DEFAULT_TIMEZONE
                                )}
                              />
                            </Box>
                            <Box sx={{ width: "130px", marginLeft: "16px" }}>
                              <SelectTime
                                label="Stop"
                                disabled={true}
                                size="small"
                                timezone={DEFAULT_TIMEZONE}
                                value={dayjs(timeEntry.stop).tz(
                                  DEFAULT_TIMEZONE
                                )}
                                minTime={dayjs(timeEntry.start).tz(
                                  DEFAULT_TIMEZONE
                                )}
                              />
                            </Box>
                          </Box>
                          <Box
                            sx={{
                              marginTop: "16px",
                              display: "flex",
                              flexDirection: "row",
                            }}
                          >
                            <TextField
                              label="Lunch Minutes"
                              disabled={true}
                              size="small"
                              sx={{ width: "130px" }}
                              type="number"
                              value={timeEntry.lunchDurationMinutes}
                            />
                            <TextField
                              label="Break Minutes"
                              disabled={true}
                              size="small"
                              sx={{ width: "130px", marginLeft: "16px" }}
                              type="number"
                              value={timeEntry.breakMinutes}
                            />
                          </Box>
                          {timeEntry.breakMinutes &&
                          timeEntry.breakMinutes > 0 ? (
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "flex-start",
                                marginTop: "16px",
                              }}
                            >
                              Break Reason: {timeEntry.breakReason}
                            </Box>
                          ) : null}
                        </Box>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}
        </Box>
      ) : (
        <TableContainer component={Box}>
          <Table sx={{}}>
            <TableHead>
              <TableRow>
                <TableCell align="left">Date</TableCell>
                <TableCell align="left">Start</TableCell>
                <TableCell align="left">Stop</TableCell>
                <TableCell align="left">Lunch Mins</TableCell>
                <TableCell align="left">Break Mins</TableCell>
                <TableCell align="left">Work Hours</TableCell>
                <TableCell align="left">No Work</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {weeklyUserTimeSummary.timeEntries &&
                weeklyUserTimeSummary.timeEntries.map((timeEntry, index) => {
                  return (
                    <TableRow key={index}>
                      <TableCell align="left">
                        {dayjs(timeEntry.date)
                          .tz(DEFAULT_TIMEZONE)
                          .format("dddd MM/DD/YY")}
                      </TableCell>
                      <TableCell align="left">
                        <Box sx={{ width: "130px" }}>
                          {!timeEntry.noWork && (
                            <SelectTime
                              disabled={true}
                              size="small"
                              timezone={DEFAULT_TIMEZONE}
                              value={dayjs(timeEntry.start).tz(
                                DEFAULT_TIMEZONE
                              )}
                              maxTime={dayjs(timeEntry.stop).tz(
                                DEFAULT_TIMEZONE
                              )}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="left">
                        <Box sx={{ width: "130px" }}>
                          {!timeEntry.noWork && (
                            <SelectTime
                              disabled={true}
                              size="small"
                              timezone={DEFAULT_TIMEZONE}
                              value={dayjs(timeEntry.stop).tz(DEFAULT_TIMEZONE)}
                              minTime={dayjs(timeEntry.start).tz(
                                DEFAULT_TIMEZONE
                              )}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="left">
                        {!timeEntry.noWork && (
                          <TextField
                            disabled={true}
                            size="small"
                            sx={{ width: "80px" }}
                            type="number"
                            value={timeEntry.lunchDurationMinutes}
                          />
                        )}
                      </TableCell>
                      <TableCell align="left">
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-start",
                          }}
                        >
                          {!timeEntry.noWork && (
                            <BreakMinutes
                              disabled={true}
                              size="small"
                              sx={{ width: "80px" }}
                              value={timeEntry.breakMinutes}
                              reason={timeEntry.breakReason}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="left">
                        {!timeEntry.noWork ? (
                          <>
                            {toThreeDecimals(
                              Number(timeEntry.workMinutes ?? 0) / 60
                            )}
                          </>
                        ) : (
                          <>0</>
                        )}
                      </TableCell>
                      <TableCell align="left">
                        <NoWorkSwitch
                          disabled={true}
                          checked={timeEntry.noWork}
                          reason={timeEntry.noWorkReason}
                        />
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* SIGNING */}
      {weeklyUserTimeSummary.adminApprovedOn && (
        <Box>
          <Box sx={{ marginTop: "32px" }}>
            <Typography>
              By signing this time sheet I certify that I have carefully
              reviewed this time sheet and that the hours reported on this time
              sheet, including all start and stop times, are accurate and I have
              not reported more or less time than I actually worked.
            </Typography>
          </Box>
          {weeklyUserTimeSummary.employeeSignedOn ? (
            <Box>
              <Box
                sx={{
                  marginTop: "16px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ fontWeight: "bold" }}>
                  Signed on{" "}
                  {weeklyUserTimeSummary.uploadedSignedSummary
                    ? "(via upload)"
                    : ""}
                  :
                </Typography>
                <Typography>
                  {dayjs(weeklyUserTimeSummary.employeeSignedOn)
                    .tz(DEFAULT_TIMEZONE)
                    .format("MM/DD/YY hh:mm:ss a")}
                </Typography>
              </Box>
              {weeklyUserTimeSummary.uploadedSignedSummary ? (
                <>
                  {weeklyUserTimeSummary.uploadedSignedSummary.split(".")[1] ===
                  "pdf" ? (
                    <S3PresignedPdf
                      s3Key={weeklyUserTimeSummary.uploadedSignedSummary}
                      downloadName={`WEEKLY_TIME_SUMMARY_${dayjs(
                        weeklyUserTimeSummary.start
                      )
                        .tz(DEFAULT_TIMEZONE)
                        .startOf("isoWeek")
                        .format("YYYY-MM-DD")} - ${dayjs(
                        weeklyUserTimeSummary.start
                      )
                        .tz(DEFAULT_TIMEZONE)
                        .endOf("isoWeek")
                        .format("YYYY-MM-DD")}.pdf`}
                    />
                  ) : (
                    <S3PresignedImage
                      s3Key={weeklyUserTimeSummary.uploadedSignedSummary}
                      maxWidth={600}
                      minWidth={600}
                      height={600}
                    />
                  )}
                </>
              ) : (
                <Box
                  sx={{ display: "flex", flexDirection: "row", width: "100%" }}
                >
                  <img
                    src={
                      "https://ezorder-public.s3.us-east-2.amazonaws.com/verified_icon.png"
                    }
                    width={70}
                    height={70}
                  />
                  <Box sx={{ flex: 1 }}>
                    <Typography>
                      {weeklyUserTimeSummary.verificationString}
                    </Typography>
                    <Box>
                      <Typography
                        sx={{
                          // Break up REALLY LONG SINGLE-WORD STRING
                          wordBreak: "break-word",
                        }}
                      >
                        {weeklyUserTimeSummary.employeeDigitalSignature}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          ) : (
            <>
              {enableSignWithPin && (
                <Box sx={{ marginTop: "16px" }}>
                  <Typography variant="h6">Sign By Pin</Typography>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      marginTop: "25px",
                    }}
                  >
                    <Link
                      href="#"
                      variant="body2"
                      sx={{
                        marginBottom: "15px",
                      }}
                      onClick={() => {
                        requestNewPin();
                      }}
                    >
                      Reset PIN
                    </Link>
                    <PinInput
                      values={pinValues}
                      onChange={(value, index, values) => {
                        setPinReady(
                          values.findIndex((element) => element === "") == -1
                        );
                        setPinValues(values);
                      }}
                      mask={true}
                      type="number"
                      size="md"
                      autoTab={true}
                      autoComplete="off"
                      autoFocus={false}
                      placeholder={""}
                    />

                    <LoadingButton
                      loading={loading}
                      size="large"
                      sx={{
                        marginTop: "16px",
                        width: "75%",
                        marginBottom: "25px",
                      }}
                      disabled={!pinReady}
                      variant="contained"
                      onClick={() => {
                        approveTime(pinValues.join(""));
                      }}
                    >
                      Approve
                    </LoadingButton>
                  </Box>
                </Box>
              )}
              {enableUploadSignedTimeSummary && (
                <Box sx={{ marginTop: "16px" }}>
                  <Typography variant="h6">
                    Upload Signed Weekly Time Summary
                  </Typography>
                  <LoadingButton
                    loading={loading}
                    component="label"
                    variant="outlined"
                    sx={{
                      width: "100%",
                      minHeight: "300px",
                      // padding: 1,
                      // display: "flex",
                      // flexDirection: "column",
                      // alignItems: "center",
                      // justifyContent: "center",
                    }}
                  >
                    <Upload />
                    Upload
                    <input
                      hidden
                      accept="image/*,.doc,.docx,.pdf"
                      type="file"
                      onChange={handleFileUpload}
                    />
                  </LoadingButton>
                </Box>
              )}
            </>
          )}
        </Box>
      )}

      <div
        style={{
          position: "fixed",
          bottom: "20px",
          right: isSmallScreen ? "20px" : "50px",
          display: "flex",
          flexDirection: "column",
          gap: 16,
        }}
      >
        <>
          <Fab
            onClick={(event) => {
              setActionsAnchor(event.currentTarget);
            }}
            color="success"
            variant={isAdmin ? "extended" : null}
          >
            {isAdmin ? "Actions" : <Download />}
          </Fab>
          <Menu
            id="demo-customized-menu"
            slotProps={{
              list: {
                "aria-labelledby": "demo-customized-button",
              },
            }}
            anchorEl={actionsAnchor}
            open={actionsAnchor}
            anchorOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            onClose={handleCloseActionMenu}
          >
            {isAdmin && (
              <>
                <MenuItem
                  disableRipple
                  variant="extended"
                  color="success"
                  onClick={() => {
                    onReOpen();
                  }}
                >
                  <LockOpen />
                  Re-open For Editing
                </MenuItem>
                <Divider sx={{ my: 0.5 }} />
              </>
            )}
            {weeklyUserTimeSummary.employeeSignedOn && (
              <>
                {weeklyUserTimeSummary.uploadedSignedSummary ? (
                  <>
                    <MenuItem
                      disableRipple
                      variant="extended"
                      color="success"
                      onClick={() => {
                        downloadUploadedSignedSummary();
                      }}
                    >
                      <Download />
                      Signed Summary File
                    </MenuItem>
                    <Divider sx={{ my: 0.5 }} />
                  </>
                ) : (
                  <>
                    <MenuItem
                      disableRipple
                      variant="extended"
                      color="success"
                      onClick={() => {
                        getPDF(true);
                      }}
                    >
                      <Download />
                      Signed Summary PDF
                    </MenuItem>
                    <Divider sx={{ my: 0.5 }} />
                  </>
                )}
              </>
            )}

            <MenuItem
              disableRipple
              variant="extended"
              color="success"
              onClick={() => {
                getPDF(false);
              }}
            >
              <Download />
              Summary PDF
            </MenuItem>
          </Menu>
        </>
      </div>
    </Box>
  );
};

export default TimeSummary;
